import React, { useCallback, useEffect, useState } from "react"
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Link,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import LinkOffIcon from "@material-ui/icons/LinkOff"
import OpenInNewIcon from "@material-ui/icons/OpenInNew"
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck"
import StorefrontIcon from "@material-ui/icons/Storefront"
import SyncIcon from "@material-ui/icons/Sync"
import VpnKeyIcon from "@material-ui/icons/VpnKey"
import { useSnackbar } from "../../../utils/notificacoes"
import { useHistory } from "react-router-dom"
import {
    apagarCredenciaisIfood,
    associarLojaIfood,
    confirmarAutenticacaoIfood,
    desassociarLojaIfood,
    gerarAutenticacaoIfood,
    importarCardapiosIfood,
    importarPedidosIfood,
    obterStatusIfood,
    removerCardapiosIfood,
    type StatusIfood,
    type UserCodeIfood,
} from "../../../services/ifoodGerenciamento"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"

export function GerenciamentoIfoodForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)
    const history = useHistory()

    const [status, setStatus] = useState<StatusIfood | null>(null)
    const [userCodeResp, setUserCodeResp] = useState<UserCodeIfood | null>(null)
    const [userCodeInput, setUserCodeInput] = useState("")

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    const auth = () => localStorage.getItem("chaveSistema")

    const carregar = useCallback(async () => {
        const autorizacao = auth()
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await obterStatusIfood(autorizacao)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao carregar integração iFood", "error")
            } else {
                setStatus(res)
            }
        } finally {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setLoading])

    useEffect(() => {
        carregar()
    }, [carregar])

    const handleGerarAutenticacao = async () => {
        const autorizacao = auth()
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await gerarAutenticacaoIfood(autorizacao)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao gerar autenticação", "error")
            } else {
                setUserCodeResp(res)
            }
        } finally {
            setLoading(false)
        }
    }

    const handleConfirmarAutenticacao = async () => {
        const autorizacao = auth()
        if (!autorizacao || !userCodeResp) return
        if (!userCodeInput.trim()) {
            alertStart("Informe o código recebido do iFood.", "error")
            return
        }
        setLoading(true)
        try {
            const res = await confirmarAutenticacaoIfood(autorizacao, {
                authorizationCodeVerifier: userCodeResp.authorizationCodeVerifier,
                userCode: userCodeInput.trim(),
            })
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao autenticar", "error")
            } else {
                setUserCodeResp(null)
                setUserCodeInput("")
                alertStart(res.mensagem || "Credenciais definidas com sucesso!", "success")
                await carregar()
            }
        } finally {
            setLoading(false)
        }
    }

    const handleApagarCredenciais = async () => {
        const autorizacao = auth()
        if (!autorizacao) return
        if (
            !window.confirm(
                "Tem certeza que deseja excluir a integração iFood? Isso irá desativar por completo a integração."
            )
        )
            return
        setLoading(true)
        try {
            const res = await apagarCredenciaisIfood(autorizacao)
            if (res && "retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao apagar credenciais", "error")
            } else {
                alertStart("Credenciais apagadas e integração desativada.", "success")
                setUserCodeResp(null)
                setUserCodeInput("")
                await carregar()
            }
        } finally {
            setLoading(false)
        }
    }

    const handleAssociar = async (merchantUUID: string) => {
        const autorizacao = auth()
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await associarLojaIfood(autorizacao, merchantUUID)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao associar loja", "error")
            } else {
                setStatus(res)
                alertStart("Loja associada.", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    const handleDesassociar = async () => {
        const autorizacao = auth()
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await desassociarLojaIfood(autorizacao)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao desassociar loja", "error")
            } else {
                setStatus(res)
                alertStart("Loja desassociada.", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    const handleImportarCardapios = async (validarCodigoPDV: boolean, atualizar: boolean) => {
        const autorizacao = auth()
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await importarCardapiosIfood(autorizacao, { validarCodigoPDV, atualizar })
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao importar cardápios", "error")
            } else {
                alertStart(res.mensagem || "Cardápio(s) importado(s).", "success")
                await carregar()
            }
        } finally {
            setLoading(false)
        }
    }

    const handleRemoverCardapios = async () => {
        const autorizacao = auth()
        if (!autorizacao) return
        if (!window.confirm("Tem certeza que deseja remover os cardápios importados?")) return
        setLoading(true)
        try {
            const res = await removerCardapiosIfood(autorizacao)
            if (res && "retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao remover cardápios", "error")
            } else {
                alertStart("Cardápios removidos.", "success")
                await carregar()
            }
        } finally {
            setLoading(false)
        }
    }

    const handleImportarPedidos = async () => {
        const autorizacao = auth()
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await importarPedidosIfood(autorizacao)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao importar pedidos", "error")
            } else {
                alertStart(res.mensagem || "Pedidos importados.", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    const credenciaisDefinidas = status?.credenciaisDefinidas ?? false
    const merchantAssociado = status?.merchantAssociado ?? false

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    <img
                        src="https://clientefiel.s3.us-east-1.amazonaws.com/logos/ifood-logo.png"
                        alt="iFood"
                        style={{ height: 28, verticalAlign: "middle", marginRight: 10 }}
                    />
                    Configurações iFood
                </Typography>

                {/* ─── Autenticação ─────────────────────────────────────────── */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <VpnKeyIcon style={{ color: "#ea1d2c", marginRight: 8 }} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Autenticação
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {!credenciaisDefinidas ? (
                            <>
                                <Typography style={{ color: "#388e3c", marginBottom: 16 }}>
                                    Para começar, vamos conectar o sistema da Cliente Fiel ao iFood da sua loja.
                                </Typography>

                                {userCodeResp ? (
                                    <Box>
                                        <Typography gutterBottom>
                                            1. Acesse o link abaixo para associar a <b>Cliente Fiel</b> ao seu{" "}
                                            <b>iFood</b>:
                                        </Typography>
                                        <Link
                                            href={userCodeResp.verificationUrlComplete}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ wordBreak: "break-all" }}
                                        >
                                            {userCodeResp.verificationUrlComplete}
                                        </Link>
                                        <Box mt={2}>
                                            <Typography gutterBottom>
                                                2. Informe o código recebido do <b>iFood</b>:
                                            </Typography>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        label="Código do iFood"
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={userCodeInput}
                                                        onChange={e => setUserCodeInput(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Button
                                                        className={classes.saveButton}
                                                        variant="contained"
                                                        onClick={handleConfirmarAutenticacao}
                                                    >
                                                        Autenticar
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Button
                                        className={classes.saveButton}
                                        variant="contained"
                                        onClick={handleGerarAutenticacao}
                                    >
                                        Gerar Autenticação
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Chip
                                        label="Credenciais definidas"
                                        style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                                    />
                                </Box>
                                {status?.interno && status?.codigoAutorizacao && (
                                    <Typography
                                        variant="caption"
                                        style={{ display: "block", color: "#90a4ae", wordBreak: "break-all", marginBottom: 12 }}
                                    >
                                        {status.codigoAutorizacao}
                                    </Typography>
                                )}
                                <Button
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleApagarCredenciais}
                                    style={{ borderRadius: 25, color: "var(--cor-erro)", borderColor: "var(--cor-erro)", textTransform: "none" }}
                                >
                                    Apagar credenciais e desativar integração
                                </Button>
                            </>
                        )}
                    </Box>
                </Paper>

                {/* ─── Lojas Associadas ─────────────────────────────────────── */}
                {credenciaisDefinidas && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <StorefrontIcon style={{ color: "#ea1d2c", marginRight: 8 }} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Lojas Associadas
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Typography style={{ color: "#388e3c", marginBottom: 8 }}>
                                Escolha uma loja para fazer a integração.
                            </Typography>
                            {status && status.merchants.length > 0 ? (
                                <List dense>
                                    {status.merchants.map(m => (
                                        <ListItem key={m.id} divider disableGutters>
                                            <ListItemText
                                                primary={<b>{m.name}</b>}
                                                secondary={m.id}
                                            />
                                            {m.associada ? (
                                                <Box display="flex" style={{ gap: 8 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<LinkOffIcon />}
                                                        onClick={handleDesassociar}
                                                        style={{ textTransform: "none" }}
                                                    >
                                                        Desassociar
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<SyncIcon />}
                                                        onClick={handleImportarPedidos}
                                                        style={{ textTransform: "none" }}
                                                    >
                                                        Importar Pedidos
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handleAssociar(m.id)}
                                                    style={{ textTransform: "none", backgroundColor: "var(--cor-botao-salvar)", color: "white" }}
                                                >
                                                    Associar
                                                </Button>
                                            )}
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography style={{ color: "#7f8c8d" }}>
                                    Nenhuma loja encontrada no iFood para estas credenciais.
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                )}

                {/* ─── Importação de Cardápios ──────────────────────────────── */}
                {credenciaisDefinidas && merchantAssociado && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <PlaylistAddCheckIcon style={{ color: "#ea1d2c", marginRight: 8 }} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Importação de Cardápios para Integração
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            {status && status.cardapiosImportados.length > 0 && (
                                <List dense>
                                    {status.cardapiosImportados.map((nome, i) => (
                                        <ListItem key={`${nome}-${i}`} disableGutters>
                                            <ListItemText primary={<b>{nome}</b>} />
                                            <Chip
                                                size="small"
                                                label="IMPORTADO"
                                                style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} mt={1}>
                                {!status?.hasCardapioImportado ? (
                                    <>
                                        <Button
                                            variant="contained"
                                            onClick={() => handleImportarCardapios(true, false)}
                                            style={{ textTransform: "none", backgroundColor: "var(--cor-botao-salvar)", color: "white", borderRadius: 25 }}
                                        >
                                            Importar Cardápio(s) — Completa
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleImportarCardapios(false, false)}
                                            style={{ textTransform: "none", borderRadius: 25 }}
                                        >
                                            Importar sem considerar códigos PDV
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="contained"
                                            onClick={() => handleImportarCardapios(true, true)}
                                            style={{ textTransform: "none", backgroundColor: "var(--cor-botao-salvar)", color: "white", borderRadius: 25 }}
                                        >
                                            Atualizar Cardápio(s) — Completa
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleImportarCardapios(false, true)}
                                            style={{ textTransform: "none", borderRadius: 25 }}
                                        >
                                            Atualizar sem considerar códigos PDV
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DeleteIcon />}
                                            onClick={handleRemoverCardapios}
                                            style={{ textTransform: "none", borderRadius: 25, color: "var(--cor-erro)", borderColor: "var(--cor-erro)" }}
                                        >
                                            Remover Cardápios
                                        </Button>
                                    </>
                                )}
                            </Box>

                            <Typography style={{ color: "var(--cor-erro)", fontSize: "0.82rem", marginTop: 16 }}>
                                Esta importação não atualiza o seu cardápio no site de pedidos ou aplicativo. Para
                                receber pedidos do iFood no painel use sempre a importação completa — não considerar
                                os códigos PDV pode trazer problemas na impressão dos pedidos.
                            </Typography>
                            <Box mt={1}>
                                <Link
                                    component="button"
                                    onClick={() => history.push("/servicos/modulo/importacao-cardapio-ifood")}
                                >
                                    Importar cardápio para o site/aplicativo (tela em migração)
                                </Link>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* ─── Pedidos Pendentes ────────────────────────────────────── */}
                {credenciaisDefinidas && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Pedidos Pendentes de Integração
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Typography style={{ color: "#388e3c", marginBottom: 12 }}>
                                Lista os pedidos iFood que falharam ao serem processados. Você pode visualizar os
                                detalhes ou tentar importá-los novamente.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => history.push("/servicos/modulo/pedidos-pendentes-integracao")}
                                style={{ textTransform: "none", borderRadius: 25, backgroundColor: "#ff9800", color: "white" }}
                            >
                                Acessar Pedidos Pendentes (tela em migração)
                            </Button>
                        </Box>
                    </Paper>
                )}

                {/* ─── Status da Loja Integrada ─────────────────────────────── */}
                {credenciaisDefinidas && status && status.merchantsStatus.length > 0 && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Status da Loja Integrada
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            {status.merchantsStatus.map((s, i) => (
                                <Box key={`${s.merchantName}-${i}`} mb={2}>
                                    <Typography variant="subtitle1">
                                        <b>{s.merchantName}</b>
                                    </Typography>
                                    <List dense>
                                        <ListItem disableGutters>
                                            <ListItemText primary={`Operação: ${s.operation ?? "—"}`} />
                                        </ListItem>
                                        <ListItem disableGutters>
                                            <ListItemText primary={`Disponível: ${s.available ? "Sim" : "Não"}`} />
                                        </ListItem>
                                        <ListItem disableGutters>
                                            <ListItemText primary={`Estado: ${s.state ?? "—"}`} />
                                        </ListItem>
                                        {(s.title || s.subtitle) && (
                                            <ListItem disableGutters>
                                                <ListItemText primary={s.title ?? ""} secondary={s.subtitle ?? ""} />
                                            </ListItem>
                                        )}
                                    </List>
                                    <Divider />
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    )
}
